<template>
  <div class="pn" :class="`pn--${tone}`" :role="tone === 'error' ? 'alert' : 'note'" data-test="panel-notice">
    <i class="pn__icon fa" :class="icon || DEFAULT_ICONS[tone]" aria-hidden="true"></i>
    <div class="pn__body"><slot /></div>
    <button
      v-if="dismissible"
      type="button"
      class="pn__x"
      aria-label="Dismiss"
      data-test="panel-notice-dismiss"
      @click="$emit('dismiss')"
    ><i class="fa fa-times" aria-hidden="true"></i></button>
  </div>
</template>

<script>
/**
 * A calm callout inside an action panel (design "Patient Action Panels", 10/9): a tinted box with an
 * icon, a sentence or two, and optionally an X. One component so the send panels' errors, "why you
 * can't send" reasons and "this becomes a link" hints all look alike.
 *
 *   tone  'info'  teal   — a hint ("Each [Form: …] turns into a link only Nilay can open")
 *         'warn'  amber  — a caution, or a reason the main button is off
 *         'error' red    — a failed send; role="alert"
 *
 * Presentational: no store, no mixin. ⛔ No prop named patNum / siteID / loading / allSettings.
 */
export default {
  name: 'PanelNotice',

  props: {
    tone: { type: String, default: 'info', validator: v => ['info', 'warn', 'error'].includes(v) },
    /** A Font Awesome class; the tone's own icon when omitted. */
    icon: { type: String, default: '' },
    dismissible: { type: Boolean, default: false },
  },

  data() {
    return {
      DEFAULT_ICONS: { info: 'fa-info-circle', warn: 'fa-exclamation-triangle', error: 'fa-exclamation-circle' },
    };
  },
};
</script>

<style lang="scss" scoped>
@import './action-panel';

.pn {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: $pp-radius;
  font-size: 13px;
  line-height: 1.45;
}

.pn__icon { flex: none; margin-top: 2px; font-size: 14px; }
.pn__body { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }

.pn--info { background: $pp-brand-soft; color: $teal-800; }
.pn--warn { background: $pp-amber-bg-2; color: $pp-amber-deep; }
.pn--error { background: $pp-red-bg; color: $pp-rose-ink; border: 1px solid $pp-red-line; }

.pn__x {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: -4px -6px -4px 0;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font-size: 14px;
  opacity: 0.7;
  cursor: pointer;

  &:hover { opacity: 1; background: rgba(12, 15, 14, 0.06); }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 1px; }

  @include ap-touch { width: 36px; height: 36px; }
}
</style>
